/**
 * Pure helpers for the subscription drift-alerts viewer — drift-type
 * vocabulary, i18n key resolution, and offset-pagination math. Kept
 * side-effect free for isolated unit testing (this module is on the
 * vitest coverage include list).
 *
 * Mirrors `wanderfit-api` `SubscriptionDriftType`
 * (`subscription-drift-alert.port.ts`).
 */

/** The drift types the API can emit, newest contract as of 2026-05-22. */
export const DRIFT_TYPES = [
  "PRICE_ID_MISMATCH_NO_PENDING",
  "CATALOG_MISS",
  "PRODUCT_REPARENT",
] as const;

export type DriftType = (typeof DRIFT_TYPES)[number];

const DRIFT_TYPE_SET: ReadonlySet<string> = new Set(DRIFT_TYPES);

/** Type guard — unknown values fall through to the `unknown` i18n bucket. */
export function isDriftType(value: string): value is DriftType {
  return DRIFT_TYPE_SET.has(value);
}

/**
 * i18n key for a drift type's short label. An unrecognised value (the
 * API added a new type the backoffice hasn't shipped copy for yet)
 * resolves to the `unknown` bucket rather than rendering a raw enum.
 */
export function driftTypeLabelKey(type: string): string {
  const bucket = isDriftType(type) ? type : "unknown";
  return `subscriptions.driftAlerts.types.${bucket}.label`;
}

/** i18n key for a drift type's longer explanation. */
export function driftTypeDescriptionKey(type: string): string {
  const bucket = isDriftType(type) ? type : "unknown";
  return `subscriptions.driftAlerts.types.${bucket}.description`;
}

/**
 * Visual severity for the drift-type badge. `PRODUCT_REPARENT` always
 * signals a Stripe Dashboard misconfiguration that mis-pools venue
 * revenue, so it reads as `critical`; the others are reconciliation
 * `warning`s.
 */
export type DriftTone = "critical" | "warning";

export function driftTypeTone(type: string): DriftTone {
  return type === "PRODUCT_REPARENT" ? "critical" : "warning";
}

/** Derived offset-pagination view. */
export interface PageInfo {
  readonly page: number;
  readonly totalPages: number;
  readonly from: number;
  readonly to: number;
  readonly hasPrev: boolean;
  readonly hasNext: boolean;
}

/** Compute the human-facing page numbers for an offset/limit window. */
export function describePage(input: {
  readonly totalCount: number;
  readonly limit: number;
  readonly offset: number;
}): PageInfo {
  const limit = Math.max(1, Math.floor(input.limit));
  const totalCount = Math.max(0, Math.floor(input.totalCount));
  const offset = Math.max(0, Math.floor(input.offset));
  const totalPages = Math.max(1, Math.ceil(totalCount / limit));
  return {
    page: Math.floor(offset / limit) + 1,
    totalPages,
    from: totalCount === 0 ? 0 : offset + 1,
    to: Math.min(offset + limit, totalCount),
    hasPrev: offset > 0,
    hasNext: offset + limit < totalCount,
  };
}

/** Format an ISO timestamp for display; echoes the input if unparseable. */
export function formatDriftTimestamp(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}
